import React, { useEffect, useState, useCallback } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
import WrapperComponent from './commonComponents/WrapperComponent';
import { url } from '../util/config';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend, ChartDataLabels);

const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};
const getToday = () => new Date().toISOString().slice(0, 10);

// ── Stat card config ─────────────────────────────────────────────────────────
const STAT_CONFIG = {
    total:      { label: 'Total',      icon: 'bi-send',           color: '#1565c0', bg: '#eff6ff', border: '#bfdbfe' },
    submitted:  { label: 'Submitted',  icon: 'bi-check-circle',   color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
    pending:    { label: 'Pending',    icon: 'bi-hourglass-split', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
    failed:     { label: 'Failed',     icon: 'bi-x-circle',       color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
    read:       { label: 'Read',       icon: 'bi-eye',             color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
    delivered:  { label: 'Delivered',  icon: 'bi-check2-all',     color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
};

const getStatConfig = (key) =>
    STAT_CONFIG[key.toLowerCase()] || {
        label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        icon: 'bi-bar-chart',
        color: '#6b7280',
        bg: '#f9fafb',
        border: '#e5e7eb',
    };

// ── Main Component ───────────────────────────────────────────────────────────
const DashboardComponent = () => {
    const [showFilter, setShowFilter] = useState(false);
    const [users, setUsers] = useState([]);
    const [senders, setSenders] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedSender, setSelectedSender] = useState('');
    const [startDate, setStartDate] = useState(getFirstDayOfMonth());
    const [endDate, setEndDate] = useState(getToday());
    const [summary, setSummary] = useState({});
    const [barData, setBarData] = useState({ labels: [], datasets: [] });
    const [pieData, setPieData] = useState({ labels: [], datasets: [{ data: [], backgroundColor: [] }] });
    const [isLoading, setIsLoading] = useState(true);
    const [isSendersLoading, setIsSendersLoading] = useState(false);

    // Fetch users for filter dropdown
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${url}/users`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error();
                const data = await response.json();
                setUsers(data || []);
            } catch { setUsers([]); }
        };
        fetchUsers();
    }, []);

    // Fetch senders filtered by selected user
    useEffect(() => {
        const fetchSenders = async () => {
            setIsSendersLoading(true);
            setSelectedSender('');
            try {
                const token = localStorage.getItem('token');
                const endpoint = selectedUser
                    ? `${url}/profiles?owner=${selectedUser}`
                    : `${url}/profiles`;
                const response = await fetch(endpoint, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error();
                const data = await response.json();
                // Filter by owner if user selected (in case backend doesn't support query param)
                const filtered = selectedUser
                    ? data.filter(p => p.owner === selectedUser || p.owner?._id === selectedUser)
                    : data;
                setSenders(filtered);
            } catch { setSenders([]); }
            finally { setIsSendersLoading(false); }
        };
        fetchSenders();
    }, [selectedUser]);

    const fetchDashboardData = useCallback(async (params = {}) => {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const body = { startdate: params.startDate || startDate, enddate: params.endDate || endDate };
        if (params.user) body.user = params.user;
        if (params.sender) body.sender = params.sender;

        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

        // All 3 in parallel
        const [summaryRes, barRes, pieRes] = await Promise.allSettled([
            fetch(`${url}/totalsummary`, { method: 'POST', headers, body: JSON.stringify(body) }),
            fetch(`${url}/barchartdata`, { method: 'POST', headers, body: JSON.stringify(body) }),
            fetch(`${url}/piechartdata`, { method: 'POST', headers, body: JSON.stringify(body) }),
        ]);

        // Summary
        try {
            if (summaryRes.status === 'fulfilled' && summaryRes.value.ok) {
                const data = await summaryRes.value.json();
                setSummary(data || {});
            } else setSummary({});
        } catch { setSummary({}); }

        // Bar chart
        try {
            if (barRes.status === 'fulfilled' && barRes.value.ok) {
                const data = await barRes.value.json();
                setBarData({
                    labels: data.map(item => item.date),
                    datasets: [
                        {
                            label: 'Submitted',
                            data: data.map(item => item.Submitted ?? 0),
                            backgroundColor: 'rgba(21, 101, 192, 0.75)',
                            borderColor: '#1565c0',
                            borderWidth: 1,
                            borderRadius: 4,
                        },
                        {
                            label: 'Failed',
                            data: data.map(item => item.Failed ?? 0),
                            backgroundColor: 'rgba(220, 38, 38, 0.65)',
                            borderColor: '#dc2626',
                            borderWidth: 1,
                            borderRadius: 4,
                        },
                    ],
                });
            } else setBarData({ labels: [], datasets: [] });
        } catch { setBarData({ labels: [], datasets: [] }); }

        // Pie chart
        try {
            if (pieRes.status === 'fulfilled' && pieRes.value.ok) {
                const data = await pieRes.value.json();
                const PIE_COLORS = ['#16a34a', '#dc2626', '#0891b2', '#d97706', '#7c3aed', '#1565c0'];
                setPieData({
                    labels: Object.keys(data),
                    datasets: [{
                        data: Object.values(data),
                        backgroundColor: PIE_COLORS,
                        hoverBackgroundColor: PIE_COLORS,
                        borderWidth: 2,
                        borderColor: '#fff',
                    }],
                });
            } else setPieData({ labels: [], datasets: [{ data: [], backgroundColor: [] }] });
        } catch { setPieData({ labels: [], datasets: [{ data: [], backgroundColor: [] }] }); }

        setIsLoading(false);
    }, [startDate, endDate]);

    useEffect(() => {
        fetchDashboardData({ user: selectedUser, sender: selectedSender, startDate, endDate });
        // eslint-disable-next-line
    }, []);

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchDashboardData({ user: selectedUser, sender: selectedSender, startDate, endDate });
        setShowFilter(false);
    };

    const handleFilterReset = () => {
        setSelectedUser('');
        setSelectedSender('');
        setStartDate(getFirstDayOfMonth());
        setEndDate(getToday());
    };

    const summaryEntries = Object.entries(summary);

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Dashboard</h5>
                        <p style={styles.pageSubtitle}>
                            {startDate} &nbsp;→&nbsp; {endDate}
                        </p>
                    </div>
                    <button
                        style={{ ...styles.filterBtn, ...(showFilter ? styles.filterBtnActive : {}) }}
                        onClick={() => setShowFilter(v => !v)}
                        title="Toggle Filters"
                    >
                        <i className="bi bi-sliders" style={{ marginRight: 6 }}></i>
                        Filters
                        {(selectedUser || selectedSender) && (
                            <span style={styles.filterDot}></span>
                        )}
                    </button>
                </div>

                {/* Filter Panel */}
                <div style={{
                    ...styles.filterPanel,
                    maxHeight: showFilter ? '300px' : '0px',
                    opacity: showFilter ? 1 : 0,
                    padding: showFilter ? '20px' : '0 20px',
                }}>
                    <form onSubmit={handleFilterSubmit} noValidate>
                        <div style={styles.filterGrid}>
                            <div style={styles.filterGroup}>
                                <label style={styles.filterLabel}>User</label>
                                <select style={styles.filterSelect} value={selectedUser}
                                    onChange={e => { setSelectedUser(e.target.value); setSelectedSender(''); }}>
                                    <option value="">All Users</option>
                                    {users.map(user => (
                                        <option key={user._id} value={user._id}>{user.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.filterGroup}>
                                <label style={styles.filterLabel}>Sender</label>
                                <select style={styles.filterSelect} value={selectedSender}
                                    onChange={e => setSelectedSender(e.target.value)}
                                    disabled={!selectedUser || isSendersLoading}>
                                    <option value="">
                                        {isSendersLoading ? 'Loading…' : 'All Senders'}
                                    </option>
                                    {senders.map((s, i) => (
                                        <option key={i} value={s.whatsapp_no}>{s.whatsapp_no}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.filterGroup}>
                                <label style={styles.filterLabel}>Start Date</label>
                                <input type="date" style={styles.filterInput} value={startDate}
                                    max={endDate} onChange={e => setStartDate(e.target.value)} />
                            </div>
                            <div style={styles.filterGroup}>
                                <label style={styles.filterLabel}>End Date</label>
                                <input type="date" style={styles.filterInput} value={endDate}
                                    min={startDate} max={getToday()} onChange={e => setEndDate(e.target.value)} />
                            </div>
                        </div>
                        <div style={styles.filterActions}>
                            <button type="button" style={styles.btnSecondary} onClick={handleFilterReset}>
                                Reset
                            </button>
                            <button type="submit" style={styles.btnPrimary}>
                                <i className="bi bi-search" style={{ marginRight: 6 }}></i>
                                Apply Filters
                            </button>
                        </div>
                    </form>
                </div>

                {/* Stat Cards */}
                {isLoading ? (
                    <div style={styles.centerState}>
                        <div className="spinner-border text-primary" style={{ width: '2.5rem', height: '2.5rem' }}></div>
                        <p style={styles.stateText}>Loading dashboard…</p>
                    </div>
                ) : (
                    <>
                        {summaryEntries.length > 0 && (
                            <div style={{
                                ...styles.statGrid,
                                gridTemplateColumns: `repeat(${Math.min(summaryEntries.length, 4)}, 1fr)`
                            }}>
                                {summaryEntries.map(([key, value]) => {
                                    const cfg = getStatConfig(key);
                                    return (
                                        <div key={key} style={{ ...styles.statCard, border: `1px solid ${cfg.border}` }}>
                                            <div style={{ ...styles.statIcon, background: cfg.bg, color: cfg.color }}>
                                                <i className={`bi ${cfg.icon}`}></i>
                                            </div>
                                            <div style={styles.statInfo}>
                                                <p style={styles.statLabel}>{cfg.label}</p>
                                                <p style={{ ...styles.statValue, color: cfg.color }}>
                                                    {Number(value).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Charts row */}
                        <div style={styles.chartsRow}>
                            {/* Bar Chart */}
                            <div style={{ ...styles.chartCard, flex: 2 }}>
                                <h6 style={styles.chartTitle}>
                                    <i className="bi bi-bar-chart" style={{ marginRight: 8, color: '#1565c0' }}></i>
                                    Daily Message Activity
                                </h6>
                                {barData.labels?.length > 0 ? (
                                    <Bar
                                        data={barData}
                                        options={{
                                            responsive: true,
                                            plugins: {
                                                legend: { position: 'top', labels: { usePointStyle: true, font: { size: 12 } } },
                                                datalabels: { display: false },
                                                tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}` } }
                                            },
                                            scales: {
                                                x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                                                y: { grid: { color: '#f3f4f6' }, ticks: { font: { size: 11 }, callback: val => val.toLocaleString() } }
                                            }
                                        }}
                                    />
                                ) : (
                                    <div style={styles.chartEmpty}>
                                        <i className="bi bi-bar-chart" style={{ fontSize: 32, color: '#d1d5db' }}></i>
                                        <p style={styles.stateText}>No data for selected period.</p>
                                    </div>
                                )}
                            </div>

                            {/* Pie Chart */}
                            <div style={{ ...styles.chartCard, flex: 1 }}>
                                <h6 style={styles.chartTitle}>
                                    <i className="bi bi-pie-chart" style={{ marginRight: 8, color: '#1565c0' }}></i>
                                    Delivery Status
                                </h6>
                                {pieData.labels?.length > 0 ? (
                                    <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Pie
                                            data={pieData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { display: true, position: 'bottom', labels: { usePointStyle: true, font: { size: 11 }, padding: 12 } },
                                                    datalabels: {
                                                        color: '#fff',
                                                        font: { weight: 'bold', size: 12 },
                                                        formatter: (value) => value > 0 ? value.toLocaleString() : '',
                                                    },
                                                    tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${ctx.parsed.toLocaleString()}` } }
                                                },
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div style={styles.chartEmpty}>
                                        <i className="bi bi-pie-chart" style={{ fontSize: 32, color: '#d1d5db' }}></i>
                                        <p style={styles.stateText}>No data for selected period.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    filterBtn: { display: 'inline-flex', alignItems: 'center', position: 'relative', padding: '8px 16px', background: 'white', color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    filterBtnActive: { border: '1.5px solid #1565c0', color: '#1565c0', background: '#eff6ff' },
    filterDot: { position: 'absolute', top: 6, right: 6, width: 7, height: 7, borderRadius: '50%', background: '#1565c0' },
    filterPanel: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', transition: 'max-height 0.35s ease, opacity 0.35s ease, padding 0.35s ease', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    filterGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    filterLabel: { fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' },
    filterSelect: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151', background: 'white' },
    filterInput: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151' },
    filterActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' },
    statGrid: { display: 'grid', gap: '16px' },
    statCard: { background: 'white', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    statIcon: { width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 },
    statInfo: { display: 'flex', flexDirection: 'column', gap: 2 },
    statLabel: { margin: 0, fontSize: '11px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' },
    statValue: { margin: 0, fontSize: '24px', fontWeight: '700' },
    chartsRow: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
    chartCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', minWidth: 0 },
    chartTitle: { margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center' },
    chartEmpty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 20px' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '80px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

export default DashboardComponent;
